/**
 * TenantErrorBoundary.
 *
 * Catches render-time errors below the tenant provider so a thrown
 * widget can't take the whole bundle down. Routes the error through
 * `observability.reportError` (logs structured JSON in dev, no-ops in
 * prod until Phase 11 wires Sentry) and renders the brand-neutral
 * `ErrorBootState` fallback. The fallback uses only default `:root`
 * tokens so it works even when the tenant provider never reached
 * `live` (no brand tokens applied yet).
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorBootState } from '../components/system/BootStates.js';
import { reportError } from '../observability.js';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class TenantErrorBoundary extends Component<Props, State> {
  public override state: State = { error: null };

  public static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      scope: 'tenant-error-boundary',
      componentStack: info.componentStack ?? undefined,
    });
  }

  public override render(): ReactNode {
    const { error } = this.state;
    if (error) {
      return <ErrorBootState error={error} />;
    }
    return this.props.children;
  }
}
